import React, {useCallback, useEffect, useRef, useState} from 'react';
import {apiPost} from '../../../services/apiClient';
import {getCurrentUserCompanyId, getRoles} from '../../../services/userRights';
import Forbidden from '../../forbidden/forbidden.jsx';
import {Button, Input} from "semantic-ui-react";
import FiltersAndTable from "./filtersAndTable";
import SvgNew from "../../../components/SvgNew";
import Info from "../../../components/Info";
import ConfirmModal from "../../../components/Modals/ConfirmModal";
import DocsModal from "./docsModal";
import GridHeader from "../../../components/GridHeader";

const AuthorParcelsTable = ({history}) => {

    const [busyCount, setBusyCount] = useState(0);
    const [disableFilters, setDisableFilters] = useState(false);
    const [lastResponseError, setLastResponseError] = useState(null);
    const [selectedParcelIds, setSelectedParcelIds] = useState([]);
    const [settings, setSettings] = useState({});
    const [copyId, setCopyId] = useState(null);
    const [docsId, setDocsId] = useState(null);

    const updateBusyCount = (inc) => {
        setBusyCount((prevState) => {
            prevState = busyCount + inc;
            return prevState;
        });
    }

    const loadData = (settings, takeCount, skipCount, needOverlay) => {
        updateBusyCount(needOverlay ? 1 : 0);
        return apiPost('parcels/list/author/all', {settings, takeCount, skipCount})
            .then(data => {
                updateBusyCount(needOverlay ? -1 : 0);
                return data;
            })
            .catch(err => {
                updateBusyCount(needOverlay ? -1 : 0);
                if (err.status > 401) {
                    setLastResponseError(err.status)
                }
            });
    }

    const downloadExcel = () => {
        updateBusyCount(1);
        apiPost('parcels/list/author/excel', {settings})
            .then(data => {
                updateBusyCount(-1);
                let fileUrl = getApiBaseUrl() + 'files/' + data.result;
                window.location.href = fileUrl;
            })
            .catch(err => {
                updateBusyCount(-1);
            });
    }

    const handleSelectedAllChange = (newChecked) => {
        if (newChecked) {
            updateBusyCount(1);
            setDisableFilters(true)
            return apiPost('parcels/list/author/ids', {settings})
                .then(data => {
                    updateBusyCount(-1);
                    setDisableFilters(false);
                    setSelectedParcelIds(data.ids);
                })
                .catch(err => {
                    updateBusyCount(-1);
                    setDisableFilters(false);
                });
        } else {
            setSelectedParcelIds([]);
        }
    }

    const handleFiltersChange = () => {
        handleSelectedAllChange(false);
    }

    let roles = getRoles();
    let isCoordinator = roles.includes('Coordinator');
    let isAuthor = roles.includes('Author') || isCoordinator;
    let companyId = getCurrentUserCompanyId();

    if (lastResponseError === 403) {
        return <Forbidden/>;
    }

    let isBusy = busyCount > 0;

    const columns = [
        {
            fieldName: 'numberAndState',
            type: 'numberAndState',
            title: 'Отправление',
            disableSort: true,
            minWidth: '230px',
            maxWidth: '230px',
        },
        {
            fieldName: 'externalNumber',
            type: 'externalNumber',
            title: 'Заказ, внешн. идент.',
            disableSort: true,
            maxWidth: '190px'
        },
        {
            fieldName: 'departureDate',
            type: 'departureDate',
            title: 'Дата отгрузки',
            disableSort: false,
            filterType: 'date',
            maxWidth: '120px'
        },
        {
            fieldName: 'deliveryDate',
            type: 'deliveryDate',
            title: 'Дата доставки',
            disableSort: false,
            filterType: 'date',
            maxWidth: '120px'
        },
        {
            fieldName: 'shipment',
            type: 'shipment',
            title: 'Отгрузка',
            disableSort: true,
        },
        {
            fieldName: 'delivery',
            type: 'delivery',
            title: 'Доставка',
            disableSort: true,
        },
        {
            fieldName: 'shipmentCompany',
            type: 'shipmentCompany',
            title: 'Перевозка',
            disableSort: true,
        },
        {
            fieldName: 'cost',
            type: 'cost',
            title: 'Финансы',
            disableSort: true,
            minWidth: '100px'
        },
        {
            fieldName: 'btns',
            type: 'btns',
            disableSort: true,
            btns: [
                (row) => <ConfirmModal
                    trigger={<Info
                        key={'createDouble'}
                        label={'Создать дубликат'}
                        position={'left'}
                        trigger={
                            <Button className='image-btn' onClick={()=>setCopyId(row.id)}>
                                <SvgNew name='createDouble'/>
                            </Button>
                        }
                        offset={[7]}
                    />}
                    label={'Копирование посылок'}
                    open={row.id === copyId}
                    setOpen={(isOpen) => setCopyId(isOpen ? row.id : null)}
                    onConfirm={() => copyParcel(row)}
                >
                    Вы действительно хотите копировать посылку?
                </ConfirmModal>,
                (row) => <DocsModal
                    trigger={<Info
                        key={'docs'}
                        label={'Документы'}
                        position={'left'}
                        trigger={
                            <Button className='image-btn' onClick={()=>setDocsId(row.id)}>
                                <SvgNew name='docs'/>
                            </Button>
                        }
                        offset={[7]}
                    />}
                    open={row.id === docsId}
                    setOpen={(isOpen) => setDocsId(isOpen ? row.id : null)}
                    form={row}
                />,
            ]
        },
    ];

    const copyParcel = (row) => {
        history.push(`/parcels/copy/${row.id}`);
    }

    const [searchId, setSearchId] = useState('');
    const [isFixed, setIsFixed] = useState(false);
    const [width, setWidth] = useState(0);

    const ro = useRef(new ResizeObserver(el => {
        setWidth(el[0].target.clientWidth);
    }));

    const resize = useCallback((container) => {
        if (container) ro.current.observe(container);
        else if (ro.current) {
            ro.current.disconnect();
            setWidth(0);
        }
    }, [ro.current]);

    const fixedScroll = (el) => {
        let scrollTop = el.scrollTop;
        setIsFixed(scrollTop > 210);
    };

    useEffect(() => {
        const el = document.getElementById('scroll-el');
        el.addEventListener('scroll', () => fixedScroll(el))
        return () => el.removeEventListener('scroll', () => fixedScroll(el))
    }, [])

    return (
        <div id='scroll-el' ref={resize} className={`new-styles new-styles__p-0 scrollable ${isFixed ? 'fixed-blocks' : ''}`}>
            <GridHeader
                width={width}
                input={<Input
                    icon='search'
                    iconPosition='left'
                    placeholder='Поиск по идентификатору'
                    value={searchId || ''}
                    onChange={(e, {value}) => setSearchId(value)}
                />}
                loading={isBusy}
                name={'Мои отправления'}
                history={history}
                canCreate={isAuthor}
                downloadExcel={downloadExcel}
            />

            <FiltersAndTable
                isFixed={isFixed}
                filtersWidth={width+'px'}
                selectedIds={selectedParcelIds}
                setSelected={(id, value) => {
                    setSelectedParcelIds(ids => {
                        value ? ids = [...(ids || []), id] : ids = ids.filter(i => i !== id);
                        return ids;
                    })
                }}
                searchId={searchId}
                setSearchId={setSearchId}
                setAll={handleSelectedAllChange}
                disableFilters={disableFilters}
                onFiltersChange={() => handleFiltersChange()}
                isBusy={isBusy}
                loadData={(settings, take, skip, needOverlay) => loadData(settings, take, skip, needOverlay)}
                gridKey="parcels"
                columns={columns}
                setSettings={(settings) => setSettings(settings)}
            />
        </div>
    );
}

export default AuthorParcelsTable;


// WEBPACK FOOTER //
// ./App/pages/parcels/table/authorParcelsTableNew.jsx